/**
 * School Calendar API endpoints
 */
import { fetchApi } from './apiBase';

// === Types ===

export interface SchoolBreak {
  id?: string;
  name: string;
  start_date: string;
  end_date: string;
}

export interface SchoolCalendar {
  id: string;
  school_name: string;
  school_district: string;
  school_city: string;
  school_state: string;
  school_year: string;
  first_day: string;
  last_day: string;
  source_url: string;
  ai_confidence: number;
  is_verified: boolean;
  breaks: SchoolBreak[];
  student_names: string[];
  created_at: string;
  updated_at: string;
}

export interface SchoolLookupResult {
  found: boolean;
  school_name: string;
  school_district: string;
  city: string;
  state: string;
  school_year: string;
  first_day: string | null;
  last_day: string | null;
  breaks: SchoolBreak[];
  confidence: number;
  source_notes: string;
}

// === School Calendars ===

export async function getSchoolCalendars(): Promise<{ calendars: SchoolCalendar[] }> {
  return fetchApi<{ calendars: SchoolCalendar[] }>('/auth/school-calendars/');
}

export async function getSchoolCalendar(id: string): Promise<SchoolCalendar> {
  return fetchApi<SchoolCalendar>(`/auth/school-calendars/${id}/`);
}

export async function lookupSchoolCalendar(
  schoolName: string,
  city?: string,
  state?: string
): Promise<SchoolLookupResult> {
  return fetchApi<SchoolLookupResult>('/auth/school-calendars/lookup/', {
    method: 'POST',
    body: JSON.stringify({
      school_name: schoolName,
      city: city || '',
      state: state || '',
    }),
  });
}

export async function saveSchoolCalendar(data: {
  school_name: string;
  school_district?: string;
  city?: string;
  state?: string;
  school_year: string;
  first_day: string;
  last_day: string;
  breaks: SchoolBreak[];
  confidence?: number;
  is_verified?: boolean;
}): Promise<SchoolCalendar> {
  return fetchApi<SchoolCalendar>('/auth/school-calendars/save/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSchoolCalendar(
  id: string,
  data: Partial<SchoolCalendar>
): Promise<SchoolCalendar> {
  return fetchApi<SchoolCalendar>(`/auth/school-calendars/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteSchoolCalendar(id: string): Promise<void> {
  await fetchApi(`/auth/school-calendars/${id}/`, { method: 'DELETE' });
}

export async function checkSchoolDay(
  calendarId: string,
  date: string
): Promise<{
  date: string;
  is_school_day: boolean;
  break_name: string | null;
  school_name: string;
}> {
  return fetchApi('/auth/school-calendars/check-day/', {
    method: 'POST',
    body: JSON.stringify({ calendar_id: calendarId, date }),
  });
}
